/**
 * Business Days Calculator for Dynamic Dates
 * Calculates delivery dates excluding weekends (Friday & Saturday)
 */

class BusinessDaysCalculator {
  constructor() {
    this.init();
  }

  /**
   * Add business days to a date, skipping Friday (5) and Saturday (6)
   * @param {Date} startDate - Starting date
   * @param {number} daysToAdd - Number of business days to add
   * @returns {Date} - Resulting date
   */
  addBusinessDays(startDate, daysToAdd) {
    const result = new Date(startDate);
    let daysAdded = 0;

    while (daysAdded < daysToAdd) {
      result.setDate(result.getDate() + 1);
      const dayOfWeek = result.getDay();
      
      // Skip Friday (5) and Saturday (6)
      if (dayOfWeek !== 5 && dayOfWeek !== 6) {
        daysAdded++;
      }
    }

    return result;
  }

  /**
   * Format date according to the specified format
   * @param {Date} date - Date to format
   * @param {string} format - Format string
   * @param {Array} dayLabels - Array of day names
   * @param {Array} monthLabels - Array of month names
   * @returns {string} - Formatted date string
   */
  formatDate(date, format, dayLabels, monthLabels) {
    const day = date.getDate();
    const month = date.getMonth();
    const dayOfWeek = date.getDay();

    const dayName = dayLabels[dayOfWeek];
    const monthName = monthLabels[month];

    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const dayWithOrdinal = getOrdinal(day);
    const dayPadded = String(day).padStart(2, '0');
    const monthPadded = String(month + 1).padStart(2, '0');

    switch (format) {
      case 'day_mm_dd':
        return `${dayName}, ${monthName} ${dayWithOrdinal}`;
      
      case 'day_dd_mm':
        return `${dayName}, ${day}. ${monthName}`;
      
      case 'mm_dd':
        return `${monthName} ${dayWithOrdinal}`;
      
      case 'dd_mm':
        return `${day}. ${monthName}`;
      
      case 'dd_mm_no_dot':
        return `${day} ${monthName}`;
      
      case 'day_dd_mm_numeric':
        return `${dayName}, ${dayPadded}. ${monthPadded}.`;
      
      case 'dd_mm_numeric':
        return `${dayPadded}. ${monthPadded}.`;
      
      default:
        return `${dayName}, ${monthName} ${dayWithOrdinal}`;
    }
  }

  /**
   * Process a single element with dynamic date
   * @param {HTMLElement} element - Element to process
   * @param {string} dateFormat - Date format
   * @param {Array} dayLabels - Day names
   * @param {Array} monthLabels - Month names
   */
  processElement(element, dateFormat, dayLabels, monthLabels) {
    const text = element.getAttribute('data-text');
    const minDays = parseInt(element.getAttribute('data-min-days')) || 0;
    const maxDays = parseInt(element.getAttribute('data-max-days')) || 0;

    if (!text) return;

    const today = new Date();
    let processedText = text;

    // Calculate start date (min days)
    if (text.includes('[start_date]') && minDays > 0) {
      const startDate = this.addBusinessDays(today, minDays);
      const formattedStart = this.formatDate(startDate, dateFormat, dayLabels, monthLabels);
      processedText = processedText.replace('[start_date]', formattedStart);
    }

    // Calculate end date (max days)
    if (text.includes('[end_date]') && maxDays > 0) {
      const endDate = this.addBusinessDays(today, maxDays);
      const formattedEnd = this.formatDate(endDate, dateFormat, dayLabels, monthLabels);
      processedText = processedText.replace('[end_date]', formattedEnd);
    }

    // Update the element content
    element.innerHTML = processedText;
  }

  /**
   * Initialize the calculator and process all dynamic-dates elements
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.processAll());
    } else {
      this.processAll();
    }
  }

  /**
   * Process all dynamic-dates elements on the page
   */
  processAll() {
    const containers = document.querySelectorAll('dynamic-dates');

    containers.forEach(container => {
      const dateFormat = container.getAttribute('data-date-format') || 'day_mm_dd';
      const dayLabelsStr = container.getAttribute('data-day-labels') || 
        'Sunday, Monday, Tuesday, Wednesday, Thursday, Friday, Saturday';
      const monthLabelsStr = container.getAttribute('data-month-labels') || 
        'January, February, March, April, May, June, July, August, September, October, November, December';

      const dayLabels = dayLabelsStr.split(',').map(s => s.trim());
      const monthLabels = monthLabelsStr.split(',').map(s => s.trim());

      // Process all elements with data-dynamic-date="true"
      const elements = container.querySelectorAll('[data-dynamic-date="true"]');
      elements.forEach(element => {
        this.processElement(element, dateFormat, dayLabels, monthLabels);
      });
    });
  }
}

// Initialize when script loads
new BusinessDaysCalculator();

// Re-initialize on Shopify theme editor events
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new BusinessDaysCalculator();
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    new BusinessDaysCalculator();
  });
}

